// Tiny DOM-building helper so page code stays close to the declarative
// shape of the SwiftUI views it was ported from, without pulling in a
// framework.

export interface ElProps {
  className?: string;
  style?: Partial<CSSStyleDeclaration>;
  text?: string;
  title?: string;
  attrs?: Record<string, string>;
  onClick?: (event: MouseEvent) => void;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.className) node.className = props.className;
  if (props.style) Object.assign(node.style, props.style);
  if (props.text != null) node.textContent = props.text;
  if (props.title) node.title = props.title;
  if (props.attrs) {
    for (const [key, value] of Object.entries(props.attrs)) node.setAttribute(key, value);
  }
  if (props.onClick) node.addEventListener("click", props.onClick as EventListener);
  for (const child of children) {
    node.append(child);
  }
  return node;
}

export function spacer(): HTMLElement {
  return el("div", { style: { flex: "1" } });
}

/** Adds tactile press feedback — a quick scale-down on pointer-press, reset on
 * release/leave. Returns the element for chaining. */
export function pressable<T extends HTMLElement>(btn: T, scale = 0.88): T {
  btn.style.transition = "transform .07s ease";
  const reset = () => { btn.style.transform = ""; };
  btn.addEventListener("pointerdown", () => { btn.style.transform = `scale(${scale})`; });
  btn.addEventListener("pointerup", reset);
  btn.addEventListener("pointerleave", reset);
  btn.addEventListener("pointercancel", reset);
  return btn;
}

export function divider(): HTMLElement {
  return el("div", { className: "divider" });
}

/** App-wide tooltips with a ~1s delay. The browser's native `title` tooltip has
 * a fixed (~1.5s) delay we can't tune, so we suppress it and render our own.
 * Any element with a `title` (or `aria-label`) automatically gets one — its
 * `title` is moved to `data-tip` on first hover so the native bubble never shows.
 * Call once per document (popup + chart). */
let tooltipInited = false;
export function initTooltips(delayMs = 1000): void {
  if (tooltipInited || typeof document === "undefined") return;
  tooltipInited = true;

  const tip = document.createElement("div");
  tip.setAttribute("role", "tooltip");
  Object.assign(tip.style, {
    position: "fixed", zIndex: "2147483000", pointerEvents: "none",
    background: "var(--bg-elevated)", color: "var(--text-primary)",
    border: "1px solid var(--border)", borderRadius: "6px",
    padding: "4px 8px", font: "var(--font-caption)", maxWidth: "240px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.16)", opacity: "0",
    transition: "opacity .1s ease", whiteSpace: "nowrap",
  } as Partial<CSSStyleDeclaration>);
  tip.style.left = "-9999px";
  let mounted = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let current: HTMLElement | null = null;

  const textFor = (el: HTMLElement): string | null => {
    // Prefer an explicit data-tip; otherwise consume the native title so the
    // browser's own tooltip never fires, and fall back to aria-label.
    const dt = el.getAttribute("data-tip");
    if (dt) return dt;
    const t = el.getAttribute("title");
    if (t) { el.setAttribute("data-tip", t); el.removeAttribute("title"); return t; }
    return el.getAttribute("aria-label");
  };
  const hide = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    current = null;
    tip.style.opacity = "0";
    tip.style.left = "-9999px";
  };
  const show = (target: HTMLElement, text: string) => {
    if (!mounted) { document.body.appendChild(tip); mounted = true; }
    tip.textContent = text;
    const r = target.getBoundingClientRect();
    tip.style.opacity = "0";
    tip.style.left = "0"; tip.style.top = "0";
    const tr = tip.getBoundingClientRect();
    let left = r.left + r.width / 2 - tr.width / 2;
    left = Math.max(6, Math.min(left, window.innerWidth - tr.width - 6));
    let top = r.bottom + 6;
    if (top + tr.height > window.innerHeight - 6) top = r.top - tr.height - 6;
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(Math.max(6, top))}px`;
    tip.style.opacity = "1";
  };

  document.addEventListener("pointerover", (e) => {
    const raw = e.target as HTMLElement | null;
    const host = raw?.closest?.("[title],[data-tip],[aria-label]") as HTMLElement | null;
    if (!host || host === current) return;
    hide();
    const text = textFor(host);
    if (!text) return;
    current = host;
    timer = setTimeout(() => { if (current === host) show(host, text); }, delayMs);
  }, true);
  document.addEventListener("pointerout", (e) => {
    const raw = e.target as HTMLElement | null;
    if (current && raw && current.contains(raw) && raw !== current) return;
    hide();
  }, true);
  document.addEventListener("pointerdown", hide, true);
  window.addEventListener("blur", hide);
}

/** An inline SVG icon from a path spec, sized to `size` px. */
export function svgIcon(pathD: string, size = 12, options: { fill?: boolean; viewBox?: string } = {}): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", options.viewBox ?? "0 0 16 16");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", pathD);
  if (options.fill) {
    path.setAttribute("fill", "currentColor");
  } else {
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.4");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
  }
  svg.appendChild(path);
  return svg;
}

/** The one canonical info glyph (ⓘ), used for every unfold-to-explain affordance
 * app-wide, so the "complexity at depth" hint always looks the same. */
export const INFO_PATH = "M8 1.7a6.3 6.3 0 100 12.6 6.3 6.3 0 000-12.6M8 7.3v3.4M8 4.7l0 0";
/** A subtle round ⓘ button: tap to unfold an explainer. Consistent across the
 * app; the caller owns the toggle + the panel it reveals. Carries a `title` so it
 * also gets the standard ~1s tooltip. */
export function infoIcon(onClick: () => void, label = "What is this?"): HTMLButtonElement {
  const b = document.createElement("button");
  Object.assign(b.style, { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", padding: "0", border: "0", borderRadius: "999px", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", flex: "none" });
  b.setAttribute("aria-label", label);
  b.setAttribute("title", label);
  b.appendChild(svgIcon(INFO_PATH, 15));
  b.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
  return b;
}

/** The one canonical close glyph, used by every x/close control app-wide. */
export const X_CLOSE_PATH = "M4 4l8 8M12 4l-8 8";

/** Canonical close (✕) button: the shared SVG x + the `.x-close` hover
 * (red on a light-red rounded square). Use this for every close/remove x so
 * they look and behave identically. */
export function closeButton(
  onClick: (e: Event) => void,
  opts: { size?: number; label?: string; title?: string } = {},
): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "x-close";
  b.setAttribute("aria-label", opts.label ?? "Close");
  if (opts.title) b.setAttribute("title", opts.title);
  b.appendChild(svgIcon(X_CLOSE_PATH, opts.size ?? 14));
  b.addEventListener("click", onClick);
  return b;
}

/** Opens the standalone chart window for a symbol (extension or dev-harness context). */
/** Opens (or focuses) the chart for a symbol as a normal browser tab in
 * the current window — founder decision 2026-07: native tabs beat popup
 * windows. One tab per symbol. The name is historical; callers are
 * everywhere and the contract is unchanged. */
// Resolved ahead of time so side-panel opens can happen synchronously
// inside a user gesture (the API refuses otherwise).
let currentWindowId: number | null = null;
if (typeof chrome !== "undefined" && chrome.windows?.getCurrent) {
  void chrome.windows.getCurrent().then((w) => (currentWindowId = w.id ?? null));
}

/** Opening a tab force-closes the toolbar popup — so any action that
 * creates a tab first docks the plugin into the side panel, which
 * persists. No-op when already running in the panel. */
export function dockIntoSidePanel(): void {
  const inPanel = new URLSearchParams(location.search).has("panel");
  const sidePanelApi = (chrome as unknown as { sidePanel?: { open(o: { windowId: number }): Promise<void> } }).sidePanel;
  if (inPanel || !sidePanelApi?.open || currentWindowId == null) return;
  try {
    void sidePanelApi.open({ windowId: currentWindowId });
  } catch {
    // Older browser or no gesture — the popup simply closes as before.
  }
}

/** v1 (MVP, D-129): open (or reuse) the chartless full-window strategy builder tab
 * for a symbol — the Setups / Build / Confirm overlay hosted in its own tab, the
 * way the main app hosts it over the chart. One builder tab, always reused. Docks
 * the plugin into the side panel first so the watchlist persists once the tab
 * takes focus, so the watchlist stays available. */
/** v1 (MVP): open (or reuse) the full-window Connections tab — the same wide
 * surface Connections had over the chart, now in its own bare tab. Docks the
 * side panel first so the app stays available once the tab takes focus. */
export function openConnectionsWindow(): void {
  dockIntoSidePanel();
  if (typeof chrome !== "undefined" && chrome.tabs?.create) {
    void (async () => {
      const url = chrome.runtime.getURL("connections.html");
      const { connectionsTabId } = await chrome.storage.local.get("connectionsTabId");
      if (typeof connectionsTabId === "number") {
        try {
          await chrome.tabs.get(connectionsTabId); // throws if gone
          await chrome.tabs.update(connectionsTabId, { active: true });
          return;
        } catch {
          // fall through and open a fresh one
        }
      }
      const created = await chrome.tabs.create({ url });
      if (created?.id != null) await chrome.storage.local.set({ connectionsTabId: created.id });
    })();
  } else {
    window.open("../connections.html", "_blank", "width=980,height=760");
  }
}

export function openBuilderWindow(symbol: string): void {
  dockIntoSidePanel();
  const query = `?symbol=${encodeURIComponent(symbol)}`;
  if (typeof chrome !== "undefined" && chrome.tabs?.create) {
    void (async () => {
      const url = chrome.runtime.getURL("builder.html") + query;
      const { builderTabId } = await chrome.storage.local.get("builderTabId");
      if (typeof builderTabId === "number") {
        try {
          await chrome.tabs.get(builderTabId); // throws if the tab is gone
          await chrome.tabs.update(builderTabId, { url, active: true });
          return;
        } catch {
          // fall through and open a fresh one
        }
      }
      const created = await chrome.tabs.create({ url });
      if (created?.id != null) await chrome.storage.local.set({ builderTabId: created.id });
    })();
  } else {
    window.open(`../builder.html${query}`, "_blank", "width=980,height=760");
  }
}

